import React, { useState } from "react";
import { Instagram, MessageSquare, Mail, MapPin, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/Section";
import { api, formatApiErrorDetail } from "../lib/api";
import { CONTACT } from "../data/contact";
import { VEHICLE_CATEGORIES } from "../data/catalog";

const SERVICES = [
  "Exterior Detail",
  "Interior Detail",
  "Full Detail",
  "One-Step Paint Correction",
  "Multi-Step Paint Correction",
  "Ceramic Coating",
  "Glass Coating",
  "Headlight Polishing",
  "Paint Chip Touch-Ups",
  "Not sure — want a recommendation",
];

const CONDITION_OPTIONS = [
  "Excellent — light maintenance",
  "Good — normal use",
  "Fair — needs a deeper clean",
  "Rough — significant work required",
];

const initial = {
  name: "",
  phone: "",
  year_make_model: "",
  zip_code: "",
  vehicle_type: "",
  service_needed: "",
  preferred_date: "",
  vehicle_condition: "",
  pet_hair: "",
  major_stains_odors: "",
  message: "",
};

const fieldClass =
  "w-full bg-transparent border-0 border-b border-gray-800 rounded-none px-0 py-3 text-base text-white placeholder:text-gray-700 focus:outline-none focus:border-white transition-colors";
const labelClass =
  "block text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-3";

const YesNoField = ({ name, label, value, onChange, testId }) => (
  <div>
    <span className={labelClass}>{label}</span>
    <div className="flex gap-3">
      {["Yes", "No"].map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testId}-${opt.toLowerCase()}`}
            onClick={() => onChange({ target: { name, value: opt } })}
            className={`flex-1 py-3 border text-[11px] font-display font-light uppercase tracking-[0.25em] transition-all ${
              active
                ? "border-white text-white bg-white/5"
                : "border-gray-800 text-gray-400 hover:border-gray-600 hover:text-gray-200"
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  </div>
);

const Contact = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setSubmitted(true);
      setForm(initial);
      toast.success("Request sent. I'll be in touch.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Submission failed"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        testId="contact-header"
        eyebrow="Contact / Request"
        title={<>Request a quote.</>}
        description="Tell me about your vehicle and the service you're interested in. You can also text me a few photos so I can provide an accurate quote."
      />

      <section className="px-5 sm:px-8 md:px-12 lg:px-20 py-16 md:py-24 border-b border-gray-900">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Contact info */}
          <div className="lg:col-span-4 space-y-10 order-2 lg:order-1">
            <div>
              <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-5">
                Direct
              </p>
              <a
                href={CONTACT.smsHref}
                data-testid="contact-info-phone"
                className="flex items-center gap-3 text-base font-light text-white hover:text-gray-300 transition-colors"
              >
                <MessageSquare strokeWidth={1} size={18} />
                Text {CONTACT.phone}
              </a>
              <a
                href={CONTACT.emailHref}
                data-testid="contact-info-email"
                className="mt-4 flex items-center gap-3 text-base font-light text-white hover:text-gray-300 transition-colors break-all"
              >
                <Mail strokeWidth={1} size={18} />
                {CONTACT.email}
              </a>
              <a
                href={CONTACT.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-info-instagram"
                className="mt-4 flex items-center gap-3 text-base font-light text-white hover:text-gray-300 transition-colors"
              >
                <Instagram strokeWidth={1} size={18} />
                {CONTACT.instagram}
              </a>
            </div>

            <div>
              <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-5">
                Service Area
              </p>
              <div className="flex items-center gap-3 text-sm font-light text-gray-200">
                <MapPin strokeWidth={1} size={18} />
                {CONTACT.serviceArea}
              </div>
              <p className="mt-3 text-xs font-light text-gray-500 leading-relaxed">
                Fully mobile — I bring my own water, power and professional equipment.
              </p>
            </div>

            <div>
              <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-500 mb-5">
                Response
              </p>
              <p className="text-sm font-light text-gray-200">
                Text is fastest. Most quote requests receive a response the same day.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="border border-gray-800 p-10 md:p-16 text-center"
              >
                <Check strokeWidth={1} size={32} className="mx-auto text-gray-200 mb-8" />
                <h2 className="font-display font-extralight uppercase tracking-[0.2em] text-white text-xl sm:text-3xl mb-4">
                  Request Received
                </h2>
                <p className="text-sm font-light text-gray-400 max-w-md mx-auto leading-relaxed">
                  Thanks for reaching out. I&apos;ll review the details and follow up by text.
                </p>
                <button
                  data-testid="contact-success-reset"
                  onClick={() => setSubmitted(false)}
                  className="mt-10 inline-flex px-8 py-3 border border-gray-700 text-[11px] font-display font-light uppercase tracking-[0.3em] text-gray-200 hover:border-white hover:text-white transition-all"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8"
              >
                <label className="block md:col-span-2">
                  <span className={labelClass}>Name *</span>
                  <input
                    data-testid="contact-input-name"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className={fieldClass}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Phone *</span>
                  <input
                    data-testid="contact-input-phone"
                    name="phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="(555) 555-5555"
                    inputMode="tel"
                    className={fieldClass}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>ZIP Code or Neighborhood *</span>
                  <input
                    data-testid="contact-input-zip"
                    name="zip_code"
                    required
                    value={form.zip_code}
                    onChange={handleChange}
                    placeholder="e.g. 19103 or Rittenhouse"
                    className={fieldClass}
                  />
                </label>
                <label className="block md:col-span-2">
                  <span className={labelClass}>Year, Make &amp; Model *</span>
                  <input
                    data-testid="contact-input-vehicle"
                    name="year_make_model"
                    required
                    value={form.year_make_model}
                    onChange={handleChange}
                    placeholder="e.g. 2021 BMW X3 M40i"
                    className={fieldClass}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Vehicle Type *</span>
                  <select
                    data-testid="contact-input-vehicle-type"
                    name="vehicle_type"
                    required
                    value={form.vehicle_type}
                    onChange={handleChange}
                    className={`${fieldClass} appearance-none`}
                  >
                    <option value="" className="bg-black" disabled>
                      Select vehicle type
                    </option>
                    {VEHICLE_CATEGORIES.map((v) => (
                      <option key={v} value={v} className="bg-black">
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className={labelClass}>Service Needed *</span>
                  <select
                    data-testid="contact-input-service"
                    name="service_needed"
                    required
                    value={form.service_needed}
                    onChange={handleChange}
                    className={`${fieldClass} appearance-none`}
                  >
                    <option value="" className="bg-black" disabled>
                      Select service
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s} className="bg-black">
                        {s}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className={labelClass}>Preferred Date</span>
                  <input
                    data-testid="contact-input-date"
                    name="preferred_date"
                    type="date"
                    value={form.preferred_date}
                    onChange={handleChange}
                    className={fieldClass}
                  />
                </label>
                <label className="block">
                  <span className={labelClass}>Vehicle Condition</span>
                  <select
                    data-testid="contact-input-condition"
                    name="vehicle_condition"
                    value={form.vehicle_condition}
                    onChange={handleChange}
                    className={`${fieldClass} appearance-none`}
                  >
                    <option value="" className="bg-black">
                      Select condition (optional)
                    </option>
                    {CONDITION_OPTIONS.map((c) => (
                      <option key={c} value={c} className="bg-black">
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="md:col-span-1">
                  <YesNoField
                    name="pet_hair"
                    label="Pet Hair?"
                    value={form.pet_hair}
                    onChange={handleChange}
                    testId="contact-input-pet-hair"
                  />
                </div>
                <div className="md:col-span-1">
                  <YesNoField
                    name="major_stains_odors"
                    label="Major Stains or Odors?"
                    value={form.major_stains_odors}
                    onChange={handleChange}
                    testId="contact-input-stains-odors"
                  />
                </div>
                <label className="block md:col-span-2">
                  <span className={labelClass}>Message</span>
                  <textarea
                    data-testid="contact-input-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Anything else I should know — pickup address, areas of concern, etc."
                    className={`${fieldClass} resize-none`}
                  />
                </label>

                <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 mt-2">
                  <p className="text-[10px] font-display font-light uppercase tracking-[0.4em] text-gray-600 max-w-xs">
                    By submitting, you agree to be contacted about your quote.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="w-full sm:w-auto px-12 py-4 border border-gray-200 text-[11px] font-display font-light uppercase tracking-[0.3em] text-white hover:bg-white hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Request a Quote"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
